import { Button, Text } from '@/ui-kit'
import CheckBox from '@/ui-kit/components/Checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/ui-kit/components/Dialog'
import { useLocalStorage } from '@/utils/useLocalStorage'
import { Trans, useLingui } from '@lingui/react/macro'
import { clsx } from 'clsx'
import { useEffect, useId, useRef, useState } from 'react'
import { SKIP_REASONS, type SkipReasonCode } from './skipReasons'

type SkipTaskDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  isSubmitting: boolean
  onConfirm: (code: SkipReasonCode) => void
}

export function SkipTaskDialog({ open, onOpenChange, projectId, isSubmitting, onConfirm }: SkipTaskDialogProps) {
  const { t } = useLingui()
  const [storedDismissed, setStoredDismissed] = useLocalStorage<boolean>(
    `nectar:skip-warning-dismissed:${projectId}`,
    false
  )
  const [selectedCode, setSelectedCode] = useState<SkipReasonCode | null>(null)
  const [showWarning, setShowWarning] = useState(false)
  const [pendingDismiss, setPendingDismiss] = useState(false)
  const wasOpenRef = useRef(false)
  const radioLabelId = useId()

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current
    wasOpenRef.current = open
    if (justOpened) {
      setSelectedCode(null)
      setShowWarning(!storedDismissed)
      setPendingDismiss(false)
    }
  }, [open, storedDismissed])

  const handleConfirm = () => {
    if (!selectedCode || isSubmitting) return
    if (showWarning && pendingDismiss) {
      setStoredDismissed(true)
    }
    onConfirm(selectedCode)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-mobile:p-4 max-mobile:py-8">
        <DialogHeader>
          <DialogTitle className="!text-xl">
            <Trans>Try another task?</Trans>
          </DialogTitle>
        </DialogHeader>

        {showWarning && (
          <div className="flex flex-col gap-3 rounded-xl border border-solid border-gray-100 p-4">
            <Text type="body2-regular" className="text-gray-80">
              <Trans>
                This task won&rsquo;t be shown to you again, and you won&rsquo;t be able to return to it. Any unsaved
                progress will be lost.
              </Trans>
            </Text>
            <CheckBox
              defaultChecked={false}
              onChange={(value) => setPendingDismiss(Boolean(value))}
              className="text-gray-70"
              label={t`Don't show this again`}
            />
          </div>
        )}

        <div className="flex flex-col gap-3">
          <Text type="body1-semibold" className="text-gray-70">
            <span id={radioLabelId}>
              <Trans>Why are you skipping?</Trans>
            </span>
          </Text>
          <div role="radiogroup" aria-labelledby={radioLabelId} className="flex flex-col gap-2">
            {SKIP_REASONS.map(({ code, label }) => {
              const selected = selectedCode === code
              return (
                <button
                  key={code}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setSelectedCode(code)}
                  className={clsx(
                    'flex w-full cursor-pointer items-center gap-3 rounded-xl border border-solid px-4 py-3 text-left transition-colors',
                    selected ? 'border-green bg-green/5' : 'hover:border-gray-90 border-gray-100'
                  )}
                >
                  <span
                    className={clsx(
                      'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-solid transition-colors',
                      selected ? 'border-green' : 'border-gray-90'
                    )}
                  >
                    {selected && <span className="bg-green block h-2.5 w-2.5 rounded-full" />}
                  </span>
                  <Text type="body2-regular" className={clsx(selected ? 'text-green' : 'text-gray-70')}>
                    {t(label)}
                  </Text>
                </button>
              )
            })}
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button size="small" variant="bordered" onClick={() => onOpenChange(false)} isDisabled={isSubmitting}>
            <Trans>Cancel</Trans>
          </Button>
          <Button size="small" onClick={handleConfirm} isDisabled={!selectedCode} isLoading={isSubmitting}>
            <Trans>Try another task</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
